import * as XLSX from 'xlsx';
import { FieldAlias } from "../interfaces/field";

export interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    data?: any[][];
}

export interface ValidationError {
    step: number;
    type: string;
    message: string;
    details?: any;
}

/**
 * Finds the column index for a given alias name in headers array
 * @param headers - Array of column headers from Excel file
 * @param aliasName - The alias name to search for
 * @returns Column index or -1 if not found
 */
export const findColumnIndex = (headers: string[], aliasName: string): number => {
    return headers.findIndex(header => 
        header === aliasName
    );
};

/**
 * Extracts unique non-empty values from a specific column
 * @param jsonData - Excel data as 2D array
 * @param columnIndex - Index of the column to extract from
 * @returns Set of unique string values
 */
export const extractUniqueValuesFromColumn = (jsonData: any[][], columnIndex: number): Set<string> => {
    const uniqueValues = new Set<string>();
    
    // Skip header row (start from index 1)
    for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i] as any[];
        
        if (columnIndex >= 0 && row[columnIndex]) {
            const value = String(row[columnIndex]).trim();
            if (value && value !== '') {
                uniqueValues.add(value);
            }
        }
    }
    
    return uniqueValues;
};

/**
 * Finds field alias by field name (supports both field_name and alias_name matching)
 * @param fieldAliases - Array of field aliases
 * @param fieldName - Field name to search for (e.g., 'loan_type', 'dpd_bucket')
 * @returns FieldAlias object or undefined if not found
 */
export const findFieldAlias = (fieldAliases: FieldAlias[], fieldName: string): FieldAlias | undefined => {
    return fieldAliases.find(alias => 
        alias.field_name === fieldName || 
        alias.alias_name === fieldName
    );
};

/**
 * Processes Excel file and returns structured data
 * @param file - Excel file to process
 * @returns Promise resolving to 2D array of Excel data
 */
export const processExcelData = (file: File): Promise<any[][]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = e.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                
                // Get the first worksheet
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                
                // Convert to JSON to extract data
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                if (jsonData.length === 0) {
                    reject(new Error('The Excel file is empty.'));
                    return;
                }
                
                resolve(jsonData as any[][]);
            } catch (error) {
                reject(new Error('Error processing Excel file. Please ensure it\'s a valid Excel file.'));
            }
        };
        
        reader.onerror = () => {
            reject(new Error('Error reading file.'));
        };
        
        reader.readAsBinaryString(file);
    });
};

/**
 * Extracts headers from Excel file
 * @param file - Excel file to process
 * @returns Promise resolving to array of header strings
 */
export const extractExcelHeaders = (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = e.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                
                // Get the first worksheet
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                
                // Get the range of the worksheet
                const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
                
                // Extract headers from the first row
                const headers: string[] = [];
                for (let col = range.s.c; col <= range.e.c; col++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
                    const cell = worksheet[cellAddress];
                    if (cell && cell.v) {
                        headers.push(String(cell.v).trim());
                    }
                }
                
                resolve(headers);
            } catch (error) {
                reject(new Error('Error processing Excel file. Please ensure it\'s a valid Excel file.'));
            }
        };
        
        reader.onerror = () => {
            reject(new Error('Error reading file.'));
        };
        
        reader.readAsBinaryString(file);
    });
};